import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Loader2, MessageSquare } from "lucide-react";
import { PublicLayout } from "@/components/site/PublicLayout";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

export default function Messages() {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const [threads, setThreads] = useState([]);
    const [active, setActive] = useState(null);
    const [msgs, setMsgs] = useState([]);
    const [text, setText] = useState("");
    const [toEmail, setToEmail] = useState(params.get("to") || "");
    const [loading, setLoading] = useState(false);

    const loadThreads = () => api.get("/messages/threads").then(({ data }) => setThreads(data));
    useEffect(() => { if (user) loadThreads(); }, [user]);
    useEffect(() => { if (active) api.get(`/messages/thread/${active.user_id}`).then(({ data }) => setMsgs(data)); }, [active]);

    if (user === null) return <div className="min-h-screen grid place-items-center bg-teal-900 text-cream">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;

    const sendNew = async () => {
        if (!toEmail || !text.trim()) { toast.error("Recipient email and message required"); return; }
        setLoading(true);
        try { await api.post("/messages", { to_email: toEmail, text }); setText(""); toast.success("Message sent"); loadThreads(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
    };
    const reply = async () => {
        if (!text.trim() || !active) return;
        try { await api.post("/messages", { to_email: active.email || toEmail, text }); setText(""); const { data } = await api.get(`/messages/thread/${active.user_id}`); setMsgs(data); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <PublicLayout>
            <section className="bg-teal-900 py-10 min-h-[70vh]">
                <div className="container-x grid lg:grid-cols-3 gap-6">
                    <aside className="card-teal rounded-2xl p-4">
                        <h2 className="font-serif text-xl text-cream mb-3">Messages</h2>
                        <div className="space-y-1 mb-4">
                            {threads.map((t) => (
                                <button key={t.user_id} onClick={() => { setActive(t); setToEmail(""); }} className={`w-full text-left px-3 py-2.5 rounded-xl ${active?.user_id === t.user_id ? "bg-teal-700" : "hover:bg-teal-800/60"}`} data-testid={`thread-${t.user_id}`}>
                                    <div className="text-cream text-sm font-medium">{t.name}</div>
                                    <div className="text-xs text-cream-soft/60 truncate">{t.last}</div>
                                </button>
                            ))}
                            {!threads.length && <p className="text-cream-soft/50 text-sm px-3">No conversations yet.</p>}
                        </div>
                        <div className="border-t border-brandborder pt-3">
                            <div className="text-xs text-cream-soft/70 mb-1">New message (recipient email)</div>
                            <Input className="bg-teal-800/50 border-brandborder text-cream" placeholder="email@example.com" value={toEmail} onChange={(e) => { setToEmail(e.target.value); setActive(null); }} data-testid="msg-to" />
                        </div>
                    </aside>
                    <div className="lg:col-span-2 card-teal rounded-2xl p-4 flex flex-col min-h-[50vh]">
                        <div className="flex-1 space-y-2 overflow-y-auto mb-3">
                            {active ? msgs.map((m, i) => (
                                <div key={i} className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${m.mine ? "ml-auto bg-gold text-teal-900" : "bg-teal-800 text-cream"}`}>{m.text}</div>
                            )) : <div className="h-full grid place-items-center text-cream-soft/50"><div className="text-center"><MessageSquare className="w-10 h-10 mx-auto mb-2" />Select a conversation or start a new one</div></div>}
                        </div>
                        <div className="flex gap-2">
                            <Input className="bg-teal-800/50 border-brandborder text-cream" placeholder="Type a message…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (active ? reply() : sendNew())} data-testid="msg-text" />
                            <button onClick={active ? reply : sendNew} disabled={loading} className="btn-gold rounded-full px-5 flex items-center gap-1.5" data-testid="msg-send">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
                        </div>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
